import { Construction, ArrowLeft, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";

// Feature category metadata for contextual messaging
const featureCategories: Record<string, { title: string; description: string }> = {
  "/cost-center/assemblies": {
    title: "Assemblies",
    description: "Build reusable assembly templates with components and option groups to streamline your estimating process.",
  },
  "/cost-center/assemblies/create": {
    title: "Create Assembly",
    description: "Build reusable assembly templates with components and option groups to streamline your estimating process.",
  },
  "/cost-center/overheads": {
    title: "Fixed Overheads",
    description: "Track and allocate fixed business costs like rent, insurance, and utilities to your pricing calculations.",
  },
  "/sales/proposals": {
    title: "Proposals & Contracts",
    description: "Create professional proposals, manage contracts, and get e-signatures from clients.",
  },
  "/scheduling/calendar": {
    title: "Calendar",
    description: "View and manage all your projects, appointments, and team schedules in one place.",
  },
  "/scheduling/appointments": {
    title: "Appointments",
    description: "Schedule site visits, client meetings, and inspections with automatic reminders.",
  },
  "/scheduling/maintenance": {
    title: "Maintenance Schedules",
    description: "Set up recurring maintenance tasks and preventive service schedules.",
  },
  "/scheduling/tasks": {
    title: "Tasks & Punch Lists",
    description: "Track project tasks, create punch lists, and manage completion status.",
  },
  "/people/team": {
    title: "Team & Crews",
    description: "Manage team members, create crews, assign roles, and set permissions.",
  },
  "/fleet/vehicles": {
    title: "Vehicles & Fleet",
    description: "Track your fleet, manage vehicle maintenance, and monitor usage.",
  },
  "/fleet/equipment": {
    title: "Tools & Equipment",
    description: "Inventory management for tools and equipment with allocation tracking.",
  },
  "/fleet/gps": {
    title: "GPS Tracking",
    description: "Real-time location tracking for vehicles and equipment.",
  },
  "/workforce/time-tracking": {
    title: "Time Tracking",
    description: "Track employee hours, breaks, and overtime with detailed reporting.",
  },
  "/workforce/time-cards": {
    title: "Mobile Time Cards",
    description: "Mobile clock-in/out for field workers with GPS verification.",
  },
  "/workforce/crew-scheduling": {
    title: "Crew Scheduling",
    description: "Assign crews to projects, manage availability, and optimize resource allocation.",
  },
  "/communication/internal": {
    title: "Internal Chat",
    description: "Team messaging with channels, direct messages, and file sharing.",
  },
  "/communication/client-portal": {
    title: "Client Portal",
    description: "Give clients access to project updates, documents, and communication.",
  },
  "/growth/leads": {
    title: "Leads",
    description: "Capture and manage potential customers through your sales pipeline.",
  },
  "/growth/campaigns": {
    title: "Marketing Campaigns",
    description: "Create and track marketing campaigns to grow your business.",
  },
  "/growth/audiences": {
    title: "Audiences & Tags",
    description: "Segment your customers and leads for targeted communication.",
  },
  "/growth/forms": {
    title: "Forms",
    description: "Build custom forms for lead capture, surveys, and data collection.",
  },
  "/growth/automations": {
    title: "Automations",
    description: "Automate repetitive tasks, follow-ups, and workflows.",
  },
  "/finances/budgets": {
    title: "Budgets vs Actual",
    description: "Compare estimated costs against actual expenses for every project.",
  },
  "/finances/payments": {
    title: "Payments",
    description: "Process payments, track receivables, and manage cash flow.",
  },
  "/finances/reports": {
    title: "Financial Reports",
    description: "Comprehensive financial reporting and analytics for your business.",
  },
  "/settings/profile": {
    title: "Profile Settings",
    description: "Manage your personal profile, preferences, and security settings.",
  },
  "/settings/compliance": {
    title: "Compliance & Certifications",
    description: "Track licenses, certifications, insurance, and compliance requirements.",
  },
  "/settings/integrations": {
    title: "Integrations",
    description: "Connect with accounting software, CRMs, and other business tools.",
  },
  "/settings/notifications": {
    title: "Notifications",
    description: "Configure email, SMS, and in-app notification preferences.",
  },
};

export default function FeatureNotAvailable() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get contextual information based on current route
  const featureInfo = featureCategories[location.pathname];
  const title = featureInfo?.title || "Feature Coming Soon";
  const description = featureInfo?.description || "We're working hard to bring you this feature. Check back soon for updates!";

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
      <div className="bg-primary/10 rounded-full p-6 mb-6">
        <Construction className="h-12 w-12 text-primary" />
      </div>
      
      <h1 className="text-2xl font-bold mb-2">{title}</h1>
      
      <p className="text-muted-foreground mb-6 max-w-md">
        {description}
      </p>

      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-8 bg-muted/50 px-4 py-2 rounded-full">
        <Sparkles className="h-4 w-4" />
        <span>Part of the Doyence platform roadmap</span>
      </div>

      <div className="flex gap-3">
        <Button onClick={() => navigate(-1)} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
        <Button onClick={() => navigate("/dashboard")}>
          Dashboard
        </Button>
      </div>
    </div>
  );
}
